/**
 * SurfaceUnavailable — THE HONEST PAGE FOR A FEATURE WHOSE TABLE DOES NOT EXIST.
 *
 * ── WHY A PAGE AND NOT A DELETION ─────────────────────────────────────────
 *
 * Thirteen surfaces query relations that are absent from the database. Verified
 * against pg_class: absent, not merely missing from the generated types. Every
 * one of their queries answers 400.
 *
 * Until now each rendered its normal empty state — "no audit entries", "no team
 * members", "no report schedules" — which told the user a fact about their
 * account that was not true. They had not failed to use these features; the
 * features had never been able to work.
 *
 * The locked rule: EMPTY STATE > FABRICATION, AND EMPTY STATES MUST BE
 * STATE-AWARE. "You have none" and "this does not exist yet" are different
 * sentences and the user is owed the true one.
 *
 * HIDDEN, NOT DELETED. The components are untouched and still compile. Making a
 * surface available again is deleting one line in App.tsx; deleting the
 * component would make it a rewrite. That is the 2026-05-13 lock's own
 * reasoning, applied more widely.
 *
 * The copy names no table, no column and no infrastructure (FOX-BIBLE XXIII).
 */
import { Link } from 'react-router-dom';
import { Compass } from 'lucide-react';
import { Tip } from '@/components/Tip';

export default function SurfaceUnavailable({
  /** What the user came here for, in their words. "Audit logs" */
  name,
  /** Where they can go instead, if there is somewhere real. */
  insteadHref = '/dashboard',
  insteadLabel = 'Back to the dashboard',
}: {
  name: string;
  insteadHref?: string;
  insteadLabel?: string;
}) {
  return (
    <div className="mx-auto max-w-lg px-6 py-16 text-center" data-foxchat-id="surface-unavailable">
      <Compass className="mx-auto mb-4 h-10 w-10 text-muted-foreground opacity-60" aria-hidden="true" />
      <Tip text="This is not an empty version of the feature. It has not been built yet, and nothing of yours is missing from it.">
        <h1 className="cursor-help text-xl font-semibold text-foreground">
          {name} is not available yet
        </h1>
      </Tip>
      <p className="mt-2 text-sm text-muted-foreground">
        This is not showing you an empty list — the feature is not switched on. You
        have not lost anything and there is nothing here to set up.
      </p>
      <Tip text="Return to the dashboard, where everything that does work lives.">
        <Link
          to={insteadHref}
          className="mt-6 inline-block rounded-lg border border-border px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted/40"
        >
          {insteadLabel}
        </Link>
      </Tip>
    </div>
  );
}
